import React from "react";

import { useNumberAssociations } from "../../hooks/useNumberAssociations";
import { NumberAssociationIdentityValue, NumberAssociationMatch } from "../../types/calls.types";

import {
  ZOHO_CRM_RECORD_URL,
  ZOHO_MODULE_BADGE_CLASS,
  ZOHO_MODULE_LABEL,
} from "./callsExplorer.constants";

// CRM data is entered with inconsistent caps ("WINSERVE CARE SERVICES LTD",
// "Amon MUTYASIRA") - normalise to title case for display. Words containing
// digits or "@" (postcodes, emails) are left as-is.
const titleCaseWord = (word: string) =>
  /[\d@]/.test(word)
    ? word
    : word
        .toLowerCase()
        .replace(/(^|[-/(])(\p{L})/gu, (_, sep: string, ch: string) => sep + ch.toUpperCase())
        .replace(/^([od])'(\p{L})/iu, (_, a: string, b: string) => `${a}'${b.toUpperCase()}`);

const toTitleCase = (value: string) => value.split(/(\s+)/).map(titleCaseWord).join("");

// Unwrap Zoho lookup objects ({ name, id }) to their name; anything else
// unexpected is dropped rather than risking a render crash.
const identityText = (value: NumberAssociationIdentityValue | undefined): string | null => {
  if (typeof value === "string") return value.trim() || null;
  if (typeof value === "number") return String(value);
  if (value && typeof value === "object" && typeof value.name === "string") {
    return value.name.trim() || null;
  }
  return null;
};

const AssociationItem: React.FC<{ match: NumberAssociationMatch }> = ({ match }) => {
  // Identity keys vary per module - first non-empty value is the title, the
  // rest are shown as secondary "Key: value" details.
  const identity = Object.entries(match.identity).flatMap(([key, value]) => {
    const text = identityText(value);
    return text ? [[key, text] as const] : [];
  });
  const title = identity.length > 0 ? toTitleCase(identity[0][1]) : match.id;
  const details = identity.slice(1);

  return (
    <a
      href={ZOHO_CRM_RECORD_URL(match.module, match.id)}
      target="_blank"
      rel="noopener noreferrer"
      className="list-group-item list-group-item-action d-flex justify-content-between align-items-start"
    >
      <div>
        <div className="d-flex align-items-center gap-2">
          <span className={`badge ${ZOHO_MODULE_BADGE_CLASS[match.module]}`}>
            {ZOHO_MODULE_LABEL[match.module]}
          </span>
          <span className="fw-bold">{title}</span>
        </div>
        {details.length > 0 && (
          <div className="small text-muted mt-1">
            {details.map(([key, value]) => (
              <span key={key} className="me-3">
                {key}: {toTitleCase(value)}
              </span>
            ))}
          </div>
        )}
        {match.fields_matched.length > 0 && (
          <div className="small text-muted">Matched on: {match.fields_matched.join(", ")}</div>
        )}
      </div>
      <span className="text-muted" aria-hidden="true">
        ↗
      </span>
    </a>
  );
};

export const NumberAssociations: React.FC<{ rawNumber: string }> = ({ rawNumber }) => {
  const { lookup, loading, error, result } = useNumberAssociations(rawNumber);

  const lookupButton = (label: string) => (
    <button
      type="button"
      className="btn btn-sm btn-outline-primary fw-bold"
      onClick={() => {
        void lookup();
      }}
    >
      {label}
    </button>
  );

  return (
    <div className="mt-3">
      <h6 className="fw-bold">Associated with</h6>
      {loading ? (
        <div className="text-muted small fw-bold">Searching CRM...</div>
      ) : error ? (
        <div className="alert alert-danger py-2 mb-0 d-flex justify-content-between align-items-center">
          <span>{error}</span>
          {lookupButton("Retry")}
        </div>
      ) : result ? (
        result.matches.length === 0 ? (
          <div className="alert alert-secondary py-2 mb-0">
            No CRM records found for this number.
          </div>
        ) : (
          <div className="list-group">
            {result.matches.map((match) => (
              <AssociationItem key={`${match.module}-${match.id}`} match={match} />
            ))}
          </div>
        )
      ) : (
        <div className="d-flex align-items-center gap-2">
          {lookupButton("Look up in CRM")}
          <span className="text-muted small">(uses 3 Zoho API credits)</span>
        </div>
      )}
    </div>
  );
};
